import { useEffect } from 'react';

const SITE_URL = 'https://hackov8.com';
const SITE_NAME = 'Hackov8';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_hackplatform/artifacts/62ajqfg5_hack.png';

export default function SEO({ 
  title = "Hackov8 - Discover & Participate in Hackathons",
  description = "Join Hackov8 to discover hackathons, build innovative projects, compete with developers worldwide, and win amazing prizes. The ultimate platform for hackathon enthusiasts.",
  keywords = "hackathon, coding competition, developer events, programming contests, tech competitions, innovation challenges, build projects, developer community, hackathon platform, online hackathons, virtual hackathons, coding events, developer hackathons",
  ogImage = DEFAULT_OG_IMAGE,
  url = SITE_URL,
  type = "website",
  article = null,
  hackathon = null
}) {
  const fullTitle = title.includes('Hackov8') ? title : `${title} | Hackov8`;
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Helper to update or create meta tag
    const updateMetaTag = (selector, attribute, content) => {
      if (!content) return;
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const attrValue = selector.match(/\["([^"]+)"\]/)?.[1] || '';
        if (attribute === 'name') {
          element.setAttribute('name', attrValue);
        } else {
          element.setAttribute('property', attrValue);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    
    // Primary Meta Tags
    updateMetaTag('meta[name="description"]', 'name', description);
    updateMetaTag('meta[name="keywords"]', 'name', keywords);
    updateMetaTag('meta[name="author"]', 'name', SITE_NAME);
    updateMetaTag('meta[name="robots"]', 'name', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    
    // Open Graph Tags
    updateMetaTag('meta[property="og:type"]', 'property', type);
    updateMetaTag('meta[property="og:url"]', 'property', url);
    updateMetaTag('meta[property="og:title"]', 'property', fullTitle);
    updateMetaTag('meta[property="og:description"]', 'property', description);
    updateMetaTag('meta[property="og:image"]', 'property', ogImage);
    updateMetaTag('meta[property="og:image:width"]', 'property', '1200');
    updateMetaTag('meta[property="og:image:height"]', 'property', '630');
    updateMetaTag('meta[property="og:site_name"]', 'property', SITE_NAME);
    updateMetaTag('meta[property="og:locale"]', 'property', 'en_US');
    
    // Twitter Card Tags
    updateMetaTag('meta[name="twitter:card"]', 'name', 'summary_large_image');
    updateMetaTag('meta[name="twitter:site"]', 'name', '@hackov8');
    updateMetaTag('meta[name="twitter:creator"]', 'name', '@hackov8');
    updateMetaTag('meta[name="twitter:title"]', 'name', fullTitle);
    updateMetaTag('meta[name="twitter:description"]', 'name', description);
    updateMetaTag('meta[name="twitter:image"]', 'name', ogImage);
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
    
    // Add JSON-LD Structured Data
    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLd);
    }
    
    // Build structured data based on page type
    let structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          "url": SITE_URL,
          "name": SITE_NAME,
          "description": "The ultimate platform for discovering and participating in hackathons worldwide",
          "publisher": {
            "@id": `${SITE_URL}/#organization`
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
            },
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          "name": SITE_NAME,
          "url": SITE_URL,
          "logo": {
            "@type": "ImageObject",
            "url": DEFAULT_OG_IMAGE,
            "width": 512,
            "height": 512
          },
          "sameAs": [
            "https://twitter.com/hackov8",
            "https://linkedin.com/company/hackov8",
            "https://github.com/hackov8"
          ]
        }
      ]
    };
    
    // Add hackathon-specific structured data
    if (hackathon) {
      structuredData["@graph"].push({
        "@type": "Event",
        "@id": `${SITE_URL}/hackathon/${hackathon.slug}`,
        "name": hackathon.title,
        "description": hackathon.description,
        "image": ogImage,
        "startDate": hackathon.event_start,
        "endDate": hackathon.event_end,
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": hackathon.location === 'online' 
          ? "https://schema.org/OnlineEventAttendanceMode" 
          : "https://schema.org/OfflineEventAttendanceMode",
        "location": hackathon.location === 'online' 
          ? {
              "@type": "VirtualLocation",
              "url": `${SITE_URL}/hackathon/${hackathon.slug}`
            }
          : {
              "@type": "Place",
              "name": hackathon.venue || "TBA",
              "address": hackathon.venue || "TBA"
            },
        "organizer": {
          "@type": "Organization",
          "name": hackathon.organizer_name || SITE_NAME,
          "url": SITE_URL
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock",
          "url": `${SITE_URL}/hackathon/${hackathon.slug}`,
          "validFrom": hackathon.registration_start
        }
      });
    }
    
    // Add WebPage structured data
    structuredData["@graph"].push({
      "@type": "WebPage",
      "@id": `${url}/#webpage`,
      "url": url,
      "name": fullTitle,
      "description": description,
      "isPartOf": {
        "@id": `${SITE_URL}/#website`
      },
      "about": {
        "@id": `${SITE_URL}/#organization`
      },
      "inLanguage": "en-US"
    });
    
    jsonLd.textContent = JSON.stringify(structuredData);
    
    // Cleanup function
    return () => {
      // Remove JSON-LD on unmount to prevent duplication
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd) {
        existingJsonLd.remove();
      }
    };
  }, [fullTitle, description, keywords, ogImage, url, type, hackathon]);
  
  return null;
}

// Helper component for breadcrumbs
export function SEOBreadcrumbs({ items }) {
  useEffect(() => {
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    };
    
    let breadcrumbScript = document.querySelector('script[data-type="breadcrumb"]');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-type', 'breadcrumb');
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbData);
    
    return () => {
      const existing = document.querySelector('script[data-type="breadcrumb"]');
      if (existing) existing.remove();
    };
  }, [items]);
  
  return null;
}
